import { IPLabel } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FilterSwitch, IFilterSwitchOption } from 'components/FilterSwitch/FilterSwitch';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import { globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import { AutomationQuestion, AutomationQuestionOption } from 'models/copilot/questionSet';
import { useTranslation } from 'react-i18next';
import { fromApiDateOptional, toApiDateOptional, toDateOnly, toLocaleDateShort } from 'utils/datetime';

// the chip key of the own date; preset ids are the platform's (o1, o2, ...)
export const otherDateChoice = '__other';

export interface IQuestionDateDraft {
  // a preset's id, or otherDateChoice
  dateChoice?: string;
  // Other on a date question
  date?: Date;
  // Other on a dateRange question
  from?: Date;
  to?: Date;
}

// the answer of a date or dateRange question: the label the user reads and the ISO date(s) the model reads
export interface IQuestionDateSelection {
  label: string;
  id: string;
}

const optionKeyOf = (option: AutomationQuestionOption, index: number): string => option.id ?? `__o${index}`;

// yyyy-MM-dd of the user's local calendar day; toApiDateOptional adds a UTC midnight time part
const toIsoDate = (date: Date | undefined): string | undefined => toApiDateOptional(date)?.substring(0, 10);

const formatIsoDate = (iso: string | undefined): string => toLocaleDateShort(fromApiDateOptional(iso));

const formatRange = (t: TFunction<string[]>, from: string, to: string): string =>
  t('copilot:Chat.Questions.DateRange', { from, to });

const findPreset = (question: AutomationQuestion, choice: string | undefined): AutomationQuestionOption | undefined =>
  (question.options ?? []).find((option, index) => optionKeyOf(option, index) === choice);

/**
 * Whether a date question has an answer: a preset, or Other with a date. A range needs both dates, To not before
 * From.
 */
export const isDateAnswered = (question: AutomationQuestion, isRange: boolean, draft: IQuestionDateDraft): boolean => {
  if (draft.dateChoice === otherDateChoice) {
    if (!isRange) return draft.date !== undefined;

    return draft.from !== undefined && draft.to !== undefined && draft.to >= draft.from;
  }

  return findPreset(question, draft.dateChoice) !== undefined;
};

/**
 * The answer of a date question: a preset's label with its ISO date (a range: from..to), or Other's locale date
 * with the same ISO form. Undefined without an answer.
 */
export const toDateSelection = (
  t: TFunction<string[]>,
  question: AutomationQuestion,
  isRange: boolean,
  draft: IQuestionDateDraft,
): IQuestionDateSelection | undefined => {
  if (!isDateAnswered(question, isRange, draft)) return undefined;

  if (draft.dateChoice === otherDateChoice) {
    if (!isRange) return { label: toLocaleDateShort(draft.date), id: toIsoDate(draft.date) ?? '' };

    return {
      label: formatRange(t, toLocaleDateShort(draft.from), toLocaleDateShort(draft.to)),
      id: `${toIsoDate(draft.from)}..${toIsoDate(draft.to)}`,
    };
  }

  const preset = findPreset(question, draft.dateChoice);
  if (!preset) return undefined;

  return { label: preset.label, id: isRange ? `${preset.from ?? ''}..${preset.to ?? ''}` : preset.date ?? '' };
};

interface IQuestionDateProps {
  question: AutomationQuestion;
  isRange: boolean;
  draft: IQuestionDateDraft;
  onChange: (change: Partial<IQuestionDateDraft>) => void;
}

/**
 * A date or dateRange question of the question card: one chip per preset, and Other, which shows the date field (a
 * range: From and To) under the chips. A date chip adds the date the server resolved it to; a range chip shows its
 * label only, as the two dates do not fit on one chip.
 */
export const QuestionDate = (props: IQuestionDateProps) => {
  const { t } = useTranslation(['copilot']);
  const { question, isRange, draft } = props;
  const today = toDateOnly(new Date());

  const options: IFilterSwitchOption<string>[] = (question.options ?? []).map((option, index) => ({
    key: optionKeyOf(option, index),
    label: isRange ? option.label : `${option.label}, ${formatIsoDate(option.date)}`,
  }));
  options.push({ key: otherDateChoice, label: t('copilot:Chat.Questions.Other') });

  const renderOther = () => {
    if (draft.dateChoice !== otherDateChoice) return null;

    if (!isRange) {
      return (
        <LocalizedDatePicker
          value={draft.date}
          onDateChange={(date) => props.onChange({ date })}
          showMonthPickerAsOverlay={false}
          format="short"
          minDate={today}
        />
      );
    }

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item grow>
          <LocalizedDatePicker
            label={t('copilot:Chat.Questions.DateFrom')}
            value={draft.from}
            onDateChange={(from) => props.onChange({ from })}
            showMonthPickerAsOverlay={false}
            format="short"
            minDate={today}
          />
        </IPStack.Item>
        <IPStack.Item grow>
          <LocalizedDatePicker
            label={t('copilot:Chat.Questions.DateTo')}
            value={draft.to}
            onDateChange={(to) => props.onChange({ to })}
            showMonthPickerAsOverlay={false}
            format="short"
            minDate={draft.from ?? today}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  return (
    <IPStack tokens={{ childrenGap: 4 }}>
      <IPLabel required={question.required === true}>{question.text}</IPLabel>
      <FilterSwitch
        options={options}
        selectedKey={draft.dateChoice}
        onChange={(dateChoice) => props.onChange({ dateChoice })}
      />
      {renderOther()}
    </IPStack>
  );
};

export default QuestionDate;
